import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { isAdminSessionExpiredError } from "../../lib/adminAuth";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Settings2, Video, Grid3X3, Users, Plus, Trash2, Webhook, KeyRound, Copy, RefreshCw } from "lucide-react";

const EMPTY_WEBHOOK = { enabled: false, url: "", secret_configured: false };
const EMPTY_INBOUND = { path: "/api/hooks/access-codes", secret_configured: false, secret: null };

const copyText = async (value, label) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copié`);
  } catch {
    toast.error("Impossible de copier");
  }
};

const CopyField = ({ label, value, hint, testId }) => (
  <div>
    <Label className="text-white/70 text-xs uppercase tracking-wide">{label}</Label>
    {hint ? <p className="text-xs text-white/40 mt-0.5 mb-1">{hint}</p> : null}
    <div className="flex gap-2 mt-1">
      <Input
        readOnly
        value={value}
        className="md-input font-mono text-xs"
        data-testid={testId}
        onFocus={(e) => e.target.select()}
      />
      <Button
        type="button"
        variant="outline"
        onClick={() => void copyText(value, label)}
        className="shrink-0 border-md-cyan/30 text-md-cyan hover:bg-md-cyan/10"
        data-testid={testId ? `${testId}-copy` : undefined}
      >
        <Copy size={14} className="mr-1" /> Copier
      </Button>
    </div>
  </div>
);

const SettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    questions_per_session: 3,
    min_questions_per_session: 1,
    max_questions_per_session: 20,
    active_questions_count: 0,
    recording_quality: "standard",
    recording_quality_presets: [],
    max_upload_mb: 200,
    team_contacts: [],
    webhook: EMPTY_WEBHOOK,
    inbound_api: EMPTY_INBOUND,
  });
  const [questionsDraft, setQuestionsDraft] = useState(3);
  const [qualityDraft, setQualityDraft] = useState("standard");
  const [contactsDraft, setContactsDraft] = useState([]);
  const [newContact, setNewContact] = useState("");
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [clearWebhookSecret, setClearWebhookSecret] = useState(false);
  const [inboundSecret, setInboundSecret] = useState("");
  const [clearInboundSecret, setClearInboundSecret] = useState(false);
  const [inboundGenerate, setInboundGenerate] = useState(false);

  const applyWebhookFromServer = (webhook) => {
    const w = webhook || EMPTY_WEBHOOK;
    setWebhookEnabled(Boolean(w.enabled));
    setWebhookUrl(w.url || "");
    setWebhookSecret("");
    setClearWebhookSecret(false);
  };

  const applyInboundFromServer = () => {
    setInboundSecret("");
    setClearInboundSecret(false);
    setInboundGenerate(false);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/game-settings");
      setSettings(data);
      setQuestionsDraft(data.questions_per_session);
      setQualityDraft(data.recording_quality);
      setContactsDraft(Array.isArray(data.team_contacts) ? data.team_contacts : []);
      applyWebhookFromServer(data.webhook);
      applyInboundFromServer();
    } catch (err) {
      if (isAdminSessionExpiredError(err)) return;
      toast.error("Erreur de chargement des paramètres");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectedPreset = useMemo(
    () => settings.recording_quality_presets.find((p) => p.id === qualityDraft),
    [settings.recording_quality_presets, qualityDraft],
  );

  const countOptions = [];
  for (let n = settings.min_questions_per_session; n <= settings.max_questions_per_session; n += 1) {
    countOptions.push(n);
  }

  const notEnoughActive = settings.active_questions_count < questionsDraft;
  const exceedsSafeQuestions =
    selectedPreset && questionsDraft > selectedPreset.estimated_max_safe_questions;
  const contactsEqual =
    contactsDraft.length === (settings.team_contacts || []).length &&
    contactsDraft.every((name, i) => name === settings.team_contacts[i]);
  const webhook = settings.webhook || EMPTY_WEBHOOK;
  const inbound = settings.inbound_api || EMPTY_INBOUND;
  const webhookDirty =
    webhookEnabled !== Boolean(webhook.enabled) ||
    webhookUrl.trim() !== String(webhook.url || "").trim() ||
    Boolean(webhookSecret.trim()) ||
    clearWebhookSecret;
  const inboundDirty =
    Boolean(inboundSecret.trim()) || clearInboundSecret || inboundGenerate;
  const dirty =
    questionsDraft !== settings.questions_per_session ||
    qualityDraft !== settings.recording_quality ||
    !contactsEqual ||
    webhookDirty ||
    inboundDirty;

  const addContact = () => {
    const name = newContact.trim().replace(/\s+/g, " ");
    if (!name) return;
    const exists = contactsDraft.some((c) => c.toLocaleLowerCase("fr") === name.toLocaleLowerCase("fr"));
    if (exists) {
      toast.error("Ce contact existe déjà");
      return;
    }
    setContactsDraft((prev) => [...prev, name]);
    setNewContact("");
  };

  const removeContact = (index) => {
    setContactsDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const save = async () => {
    setSaving(true);
    try {
      const webhookPayload = {
        enabled: webhookEnabled,
        url: webhookUrl.trim(),
      };
      if (clearWebhookSecret) {
        webhookPayload.clear_secret = true;
      } else if (webhookSecret.trim()) {
        webhookPayload.secret = webhookSecret.trim();
      }
      const inboundPayload = {};
      if (inboundGenerate) {
        inboundPayload.generate = true;
      } else if (clearInboundSecret) {
        inboundPayload.clear_secret = true;
      } else if (inboundSecret.trim()) {
        inboundPayload.secret = inboundSecret.trim();
      }
      const { data } = await api.put("/admin/game-settings", {
        questions_per_session: questionsDraft,
        recording_quality: qualityDraft,
        team_contacts: contactsDraft,
        webhook: webhookPayload,
        ...(Object.keys(inboundPayload).length ? { inbound_api: inboundPayload } : {}),
      });
      setSettings(data);
      setQuestionsDraft(data.questions_per_session);
      setQualityDraft(data.recording_quality);
      setContactsDraft(Array.isArray(data.team_contacts) ? data.team_contacts : []);
      applyWebhookFromServer(data.webhook);
      applyInboundFromServer();
      toast.success("Paramètres enregistrés");
    } catch (err) {
      if (isAdminSessionExpiredError(err)) return;
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-white/50 text-sm">Chargement des paramètres…</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="md-card p-5" data-testid="recording-quality-card">
        <div className="flex items-start gap-3 mb-4">
          <Video className="text-md-cyan shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-bold text-lg text-white">Qualité vidéo</h3>
            <p className="text-sm text-white/55 mt-1">
              Débit d&apos;enregistrement des candidats (webcam + plateau). Une qualité plus basse produit des
              fichiers plus légers — vous pouvez ainsi augmenter le nombre de questions sans dépasser la limite
              d&apos;upload ({settings.max_upload_mb} Mo).
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {settings.recording_quality_presets.map((preset) => (
            <label
              key={preset.id}
              className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${
                qualityDraft === preset.id
                  ? "border-md-cyan/50 bg-md-cyan/10"
                  : "border-white/10 bg-white/[0.02] hover:border-white/20"
              }`}
              data-testid={`quality-preset-${preset.id}`}
            >
              <input
                type="radio"
                name="recording_quality"
                value={preset.id}
                checked={qualityDraft === preset.id}
                onChange={() => setQualityDraft(preset.id)}
                className="mt-1 accent-[#4FC3F7]"
              />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-white">{preset.label}</div>
                <p className="text-sm text-white/55 mt-0.5">{preset.description}</p>
                <p className="text-xs text-white/40 mt-2">
                  ~{preset.estimated_mb_per_minute} Mo/min · conseillé : jusqu&apos;à{" "}
                  {preset.recommended_max_questions} questions · marge upload : ~
                  {preset.estimated_max_safe_questions} questions max
                </p>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="md-card p-5" data-testid="game-settings-card">
        <div className="flex items-start gap-3 mb-4">
          <Grid3X3 className="text-md-cyan shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-bold text-lg text-white">Nombre de questions</h3>
            <p className="text-sm text-white/55 mt-1">
              Questions tirées au hasard pour chaque nouveau candidat (parmi les questions actives).
            </p>
          </div>
        </div>

        <div className="sm:w-48">
          <Label className="text-white/70">Questions par partie</Label>
          <select
            value={questionsDraft}
            onChange={(e) => setQuestionsDraft(Number(e.target.value))}
            className="md-input mt-1 w-full py-2 min-h-10 box-border leading-normal"
            data-testid="questions-per-session-select"
          >
            {countOptions.map((n) => (
              <option key={n} value={n}>
                {n} {n === 1 ? "question" : "questions"}
              </option>
            ))}
          </select>
        </div>

        <p className="text-xs text-white/45 mt-3">
          Banque active :{" "}
          <span className="text-white/80 font-semibold">{settings.active_questions_count}</span> question(s)
        </p>
        {notEnoughActive && (
          <p className="text-xs text-amber-400/90 mt-2" data-testid="not-enough-active-warning">
            Il faut au moins {questionsDraft} question(s) active(s) pour démarrer une partie.
          </p>
        )}
        {exceedsSafeQuestions && selectedPreset && (
          <p className="text-xs text-amber-400/90 mt-2" data-testid="quality-questions-warning">
            Avec la qualité « {selectedPreset.label} », {questionsDraft} questions risquent de produire une
            vidéo proche ou au-delà de {settings.max_upload_mb} Mo. Privilégiez moins de questions ou une
            qualité plus économique.
          </p>
        )}
      </div>

      <div className="md-card p-5" data-testid="team-contacts-card">
        <div className="flex items-start gap-3 mb-4">
          <Users className="text-md-cyan shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-bold text-lg text-white">Contacts équipe</h3>
            <p className="text-sm text-white/55 mt-1">
              Membres assignables dans la colonne « Contact » du tableau Sessions &amp; vidéos, pour le suivi
              des candidatures.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <Input
            value={newContact}
            onChange={(e) => setNewContact(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addContact();
              }
            }}
            placeholder="Prénom ou nom du membre"
            className="md-input flex-1"
            data-testid="team-contact-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={addContact}
            className="border-md-cyan/30 text-md-cyan hover:bg-md-cyan/10"
            data-testid="team-contact-add"
          >
            <Plus size={16} className="mr-1" /> Ajouter
          </Button>
        </div>

        {contactsDraft.length === 0 ? (
          <p className="text-sm text-white/45">Aucun contact pour le moment.</p>
        ) : (
          <ul className="space-y-2" data-testid="team-contacts-list">
            {contactsDraft.map((name, index) => (
              <li
                key={`${name}-${index}`}
                className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2"
              >
                <span className="text-white text-sm">{name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeContact(index)}
                  className="h-8 text-red-300 hover:text-white hover:bg-red-500/15"
                  data-testid={`team-contact-remove-${index}`}
                >
                  <Trash2 size={14} />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="md-card p-5" data-testid="webhook-settings-card">
        <div className="flex items-start gap-3 mb-4">
          <Webhook className="text-md-cyan shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-bold text-lg text-white">Webhook résultats</h3>
            <p className="text-sm text-white/55 mt-1">
              Après réception de la vidéo composite, envoie un POST JSON (
              <code className="text-white/70">moneydrop.session.finished</code>) vers votre API pour
              synchroniser les résultats. Une seule tentative automatique ; renvoi manuel depuis Sessions.
            </p>
          </div>
        </div>

        <label className="flex items-center gap-3 cursor-pointer mb-4">
          <input
            type="checkbox"
            checked={webhookEnabled}
            onChange={(e) => setWebhookEnabled(e.target.checked)}
            className="accent-[#4FC3F7] size-4"
            data-testid="webhook-enabled"
          />
          <span className="text-sm text-white">Activer l&apos;envoi webhook</span>
        </label>

        <div className="space-y-3">
          <div>
            <Label className="text-white/70">URL HTTPS</Label>
            <Input
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://votre-api.exemple.com/webhooks/moneydrop"
              className="md-input mt-1"
              data-testid="webhook-url"
              autoComplete="off"
            />
          </div>
          <div>
            <Label className="text-white/70">
              Secret Bearer{" "}
              {webhook.secret_configured && !clearWebhookSecret ? (
                <span className="text-emerald-400/80 font-normal">(déjà configuré)</span>
              ) : null}
            </Label>
            <Input
              type="password"
              value={webhookSecret}
              onChange={(e) => {
                setWebhookSecret(e.target.value);
                if (e.target.value.trim()) setClearWebhookSecret(false);
              }}
              placeholder={
                webhook.secret_configured && !clearWebhookSecret
                  ? "Laisser vide pour conserver · saisir pour remplacer"
                  : "Optionnel — envoyé en Authorization: Bearer"
              }
              className="md-input mt-1"
              data-testid="webhook-secret"
              autoComplete="new-password"
              disabled={clearWebhookSecret}
            />
            {webhook.secret_configured ? (
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={clearWebhookSecret}
                  onChange={(e) => {
                    setClearWebhookSecret(e.target.checked);
                    if (e.target.checked) setWebhookSecret("");
                  }}
                  className="accent-[#4FC3F7]"
                  data-testid="webhook-clear-secret"
                />
                <span className="text-xs text-white/55">Effacer le secret enregistré</span>
              </label>
            ) : null}
          </div>
        </div>
      </div>

      <div className="md-card p-5" data-testid="inbound-api-settings-card">
        <div className="flex items-start gap-3 mb-4">
          <KeyRound className="text-md-cyan shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-bold text-lg text-white">Webhook création de codes</h3>
            <p className="text-sm text-white/55 mt-1">
              Collez cette URL dans votre base / outil externe. Méthode{" "}
              <strong className="text-white/80 font-medium">POST</strong>. Envoyez n&apos;importe
              quel JSON : la première valeur utilisable devient le code d&apos;accès (le nom du
              champ n&apos;a pas d&apos;importance).
            </p>
          </div>
        </div>

        {inbound.secret_configured && inbound.secret && !clearInboundSecret ? (
          <div className="space-y-4 mb-4">
            <CopyField
              label="URL du webhook"
              hint="Si votre outil gère les en-têtes HTTP (Authorization ou X-Webhook-Secret)"
              value={`${typeof window !== "undefined" ? window.location.origin : ""}${inbound.path || "/api/hooks/access-codes"}`}
              testId="inbound-hook-url"
            />
            <CopyField
              label="URL avec secret (1 seul champ)"
              hint="Pour les outils qui n'acceptent qu'une URL, sans en-tête custom"
              value={`${typeof window !== "undefined" ? window.location.origin : ""}${inbound.path || "/api/hooks/access-codes"}?secret=${encodeURIComponent(inbound.secret)}`}
              testId="inbound-hook-url-secret"
            />
            <CopyField
              label="Secret seul (en-tête HTTP)"
              hint="Authorization: Bearer … · ou X-Webhook-Secret · ou ?secret="
              value={inbound.secret}
              testId="inbound-hook-secret"
            />
          </div>
        ) : (
          <p className="text-sm text-amber-300/90 mb-4">
            Aucun webhook actif. Générez un secret pour obtenir l&apos;URL à coller dans votre outil.
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setInboundGenerate(true);
              setClearInboundSecret(false);
              setInboundSecret("");
            }}
            className="border-md-cyan/30 text-md-cyan hover:bg-md-cyan/10"
            data-testid="inbound-generate-secret"
          >
            <RefreshCw size={14} className="mr-1" />
            {inbound.secret_configured ? "Régénérer le secret" : "Générer le webhook"}
          </Button>
          {inbound.secret_configured ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setClearInboundSecret(true);
                setInboundGenerate(false);
                setInboundSecret("");
              }}
              className="text-red-300/80 hover:text-white hover:bg-red-500/15"
              data-testid="inbound-clear-secret"
            >
              Désactiver
            </Button>
          ) : null}
        </div>
        {(inboundGenerate || clearInboundSecret) && (
          <p className="text-xs text-md-cyan/90 mt-3">
            {inboundGenerate
              ? "Nouveau secret prêt — cliquez sur « Enregistrer les paramètres » pour l’activer."
              : "Désactivation en attente — cliquez sur « Enregistrer les paramètres »."}
          </p>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Button
          onClick={save}
          disabled={saving || !dirty}
          className="md-btn-solid"
          data-testid="save-game-settings-button"
        >
          <Settings2 size={16} className="mr-2" />
          {saving ? "Enregistrement…" : "Enregistrer les paramètres"}
        </Button>
        {!dirty && (
          <span className="text-xs text-white/40">Les paramètres actuels sont déjà appliqués.</span>
        )}
      </div>
    </div>
  );
};

export default SettingsTab;
